import { ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import classNames from "classnames"
import React, { useState, useRef, PropsWithChildren, ReactNode, HTMLProps } from "react"

type Position =
    | "top-left"
    | "top-center"
    | "top-right"
    | "center-left"
    | "center-center"
    | "center-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right"

const getPositionProps = (position: Position): { className: string; transform: string } => {
    switch (position) {
        case "top-left":
            return {
                className: "top-0 left-0",
                transform: "translateY(-100%)",
            }
        case "top-center":
            return {
                className: "top-0 left-1/2",
                transform: "translate(-50%, -100%)",
            }
        case "top-right":
            return {
                className: "top-0 right-0",
                transform: "translateY(-100%)",
            }

        case "center-left":
            return {
                className: "top-1/2 left-0",
                transform: "translate(-100%, -50%)", // Moves it to the left of the container
            }
        case "center-center":
            return {
                className: "top-1/2 left-1/2",
                transform: "translate(-50%, -50%)", // Dead center
            }
        case "center-right":
            return {
                className: "top-1/2 right-0",
                transform: "translate(100%, -50%)", // Moves it to the right of the container
            }
        case "bottom-left":
            return {
                className: "right-0",
                transform: "none",
            }
        case "bottom-center":
            return {
                className: "left-1/2",
                transform: "translateX(-50%)",
            }
        case "bottom-right":
            return {
                className: "left-0",
                transform: "none",
            }
        default:
            return {
                className: "left-1/2",
                transform: "translateX(-50%)",
            }
    }
}

const PopupRoot = ({
    children,
    content,
    position = "bottom-center",
    portalId,
}: PropsWithChildren<{ content: ReactNode; position?: Position; portalId?: string }>) => {
    const [isOpen, setIsOpen] = useState(false)
    const popupRef = useRef<HTMLDivElement>(null)

    const handleClickOutside = (event: MouseEvent) => {
        if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
            setIsOpen(false)
        }
    }

    const { className: positionClass, transform: positionTransform } = getPositionProps(position)

    return (
        <div className="relative max-h-min" ref={popupRef}>
            <div onClick={() => setIsOpen(!isOpen)} className="flex items-center">
                {children}
            </div>

            {isOpen && (
                <ClickAwayListenerV2
                    onClickAway={handleClickOutside}
                    portalId={portalId}
                    className={`absolute top-full mt-2 ${positionClass}`}
                    style={{
                        transform: positionTransform,
                        zIndex: 999,
                    }}
                >
                    {content}
                </ClickAwayListenerV2>
            )}
        </div>
    )
}

const PopupModal = ({
    children,
    className,
    ...rest
}: PropsWithChildren<HTMLProps<HTMLDivElement>>) => {
    return (
        <div
            className={classNames(
                "bg-black-900 border border-gray-700 rounded-xl text-gray-0 p-4 pt-3 min-w-max",
                className
            )}
            {...rest}
        >
            {children}
        </div>
    )
}

export const Popup = Object.assign(PopupRoot, { PopupModal })
